import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import colors from './colors';

/**
 * Notice shown above scheduling settings that UnifiedFlow now owns.
 *
 * Every shop this site can edit serves its schedule from UnifiedFlow, so the
 * fields below still save to this database but nothing reads them any more —
 * a silent no-op. The notice explains that, and the screens that render it
 * also disable their inputs so the edit cannot be made in the first place.
 *
 * The copy lives here rather than at each screen so the three places that
 * show it cannot drift apart.
 */

const Bar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  margin: 12px 0;
  padding: 12px 16px;
  border: 1px solid ${colors.warning};
  border-left: 4px solid ${colors.warning};
  border-radius: 4px;
  background-color: #fdf6e9;
  color: #4a4a4a;
  font-family: Lato, serif;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.3px;
`;

const Label = styled.span`
  font-weight: 700;
  white-space: nowrap;
  color: ${colors.warning};
`;

/** Per-shop hours, holidays and booking limits. */
export const LOCATION_SCHEDULING_NOTICE =
  'Hours, holidays and booking limits are now set in UnifiedFlow, under Shops → Edit → Scheduling. ' +
  'Changes saved on this page no longer have an effect.';

/**
 * Organization- and region-level holidays. Deliberately does NOT point at a
 * UnifiedFlow screen for company-wide entry — there isn't one yet, and sending
 * someone to a page that cannot do the job is worse than saying so plainly.
 */
export const TOP_LEVEL_HOLIDAYS_NOTICE =
  'Adding a holiday on this page no longer has an effect. To close shops for a holiday, ' +
  'add the date to each shop in UnifiedFlow, under Shops → Edit → Scheduling.';

const UnifiedFlowNotice = ({ children }) => {
  if (!children) return null;
  return (
    <Bar role="status">
      <Label>Managed in UnifiedFlow</Label>
      <span>{children}</span>
    </Bar>
  );
};

UnifiedFlowNotice.propTypes = {
  children: PropTypes.node,
};

UnifiedFlowNotice.defaultProps = {
  children: null,
};

export default UnifiedFlowNotice;
